import React, { useEffect, useRef, useState } from 'react';
import { Button, CircularProgress, FormControlLabel, FormLabel, Grid, MenuItem, Paper, Radio, RadioGroup, Select, Stack, TextField, Typography } from "@mui/material";
import _ from "lodash";
import Div from "@jumbo/shared/Div";
import companyServices from 'app/services/company-services';
import ImageOrBlank from 'app/widgets/Images/ImageOrBlank';
import { HOST } from 'app/utils/constants/paths';
import { isValidPhone, normalizePhoneInput } from 'app/utils/phone';
import { SaveButton } from 'app/shared/ui';

const ALLOWED_LOGO_CONTENT_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/avif'];

const SettingsCard = ({title, children, minContentHeight = 200, isLoading = false}) => {
    return (
        <Paper variant="outlined" sx={{mb: 2, p: 2.25}}>
            <Typography variant="h4" sx={{mb: 2}}>
                {title}
            </Typography>
            <Div sx={{minHeight: minContentHeight}}>
                <Div sx={{display: isLoading ? 'block' : 'none', textAlign: 'center', my: 5}}>
                    <CircularProgress/>
                </Div>
                {children}
            </Div>
        </Paper>
    )
}

const EditCompany = (props) => {
    const {company_id} = props;
    const [settings, setSettings] = useState(null);
    const [isLoading, setIsLoading] = useState(false)
    const [selectedLogo, setSelectedLogo] = useState()
    const [previewLogo, setPreviewLogo] = useState()
    const [logoError, setLogoError] = useState()
    const [saving, setSaving] = useState(false)
    const inputCompanyLogo = useRef(null);

    useEffect(() => {
        if(isLoading){
            loadCompanySettings()
        }
    }, [isLoading])

    useEffect(() => {
        setIsLoading(true)
    }, [])

    useEffect(() => {
        if (!selectedLogo) {
            setPreviewLogo(undefined)
            return
        }

        const objectUrl = URL.createObjectURL(selectedLogo)
        setPreviewLogo(objectUrl)

        // free memory when ever this component is unmounted
        return () => URL.revokeObjectURL(objectUrl)
    }, [selectedLogo])

    const loadCompanySettings = () => {
        if(company_id) {
            companyServices.getCompanySettings(company_id)
                .then(data => {
                    setSettings(data)
                    if(data?.logo_url) {
                        setPreviewLogo(`${HOST}${data.logo_url}`)
                    }
                })
                .catch(function (error) {})
                .finally(() => {
                    setIsLoading(false)
                });
        }
    }

    const saveCompanySettings = () => {        
        if(settings) {
            setSaving(true)
            
            companyServices.saveCompanySettings(company_id, {...settings, new_logo: selectedLogo})
                .then(data => {
                    setSettings(data)
                    if(data?.logo_url) {
                        setPreviewLogo(`${HOST}${data.logo_url}`)
                    }
                })
                .catch(function (error) {})
                .finally(() => {
                    setSaving(false)
                    window.location.reload();
                });
        }
    }

    const renderSelectOpts = (opts) => {
        return opts.map((item) => (<MenuItem key={item} value={item}>{item}</MenuItem>))
    }

    const onSelectLogo = e => {
        if (!e.target.files || e.target.files.length === 0) {
            setSelectedLogo(undefined)
            return
        }

        const file = e.target.files[0]
        if (!ALLOWED_LOGO_CONTENT_TYPES.includes(file.type)) {
            setSelectedLogo(undefined)
            setLogoError('El logo debe ser una imagen JPEG, PNG, GIF, WebP o AVIF.')
            e.target.value = ''
            return
        }

        setLogoError(undefined)
        setSelectedLogo(file)
    }

    const invalidContactPhone = !!settings?.contact_phone && !isValidPhone(settings?.contact_phone)
    const invalidWhatsappPhone = !!settings?.whatsapp_number && !isValidPhone(settings?.whatsapp_number)

    const renderCompanySettings = () => {
        if(settings) {
            return (
                <React.Fragment>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6} lg={2} textAlign={"center"}>
                            <Stack direction="column" justifyContent="center" alignItems="center" spacing={1}>
                                <Button onClick={() => { inputCompanyLogo.current.click(); } }>Cambiar logo</Button>
                                <ImageOrBlank src={previewLogo} size="100" style={{maxWidth: 150}} />
                                <input type='file' id='company-logo' ref={inputCompanyLogo} style={{display: 'none'}} accept={ALLOWED_LOGO_CONTENT_TYPES.join(',')} onChange={onSelectLogo}/>
                                {logoError && <Typography color="error" variant="caption">{logoError}</Typography>}
                            </Stack>
                        </Grid>
                        <Grid item xs={12} md={6} lg={4}>
                            <Typography variant="h6" sx={{mb: 2}}>Datos de la empresa</Typography>

                            <Grid container spacing={3} alignItems={"baseline"} sx={{mb: 2}}>
                                <Grid item xs={6} lg={4}>
                                    <FormLabel>Nombre</FormLabel>
                                </Grid>
                                <Grid item xs={6} lg={8}>
                                    <TextField
                                        autoFocus
                                        fullWidth
                                        error={false}
                                        variant="outlined"
                                        value={settings.name}
                                        onChange={(e) => setSettings({...settings, name: e.target.value})}
                                    />
                                </Grid>
                            </Grid>

                            <Grid container spacing={3} alignItems={"baseline"} sx={{mb: 2}}>
                                <Grid item xs={6} lg={4}>
                                    <FormLabel>TIN</FormLabel>
                                </Grid>
                                <Grid item xs={6} lg={8}>
                                    <TextField
                                        fullWidth
                                        error={false}
                                        variant="outlined"
                                        helperText="Tax Identification Number"
                                        value={settings.tin}
                                        onChange={(e) => setSettings({...settings, tin: e.target.value})}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>

                        <Grid item xs={12} md={6} lg={4}>
                            <Typography variant="h6" sx={{mb: 2}}>Datos de contacto</Typography>

                            <Grid container spacing={3} alignItems={"baseline"} sx={{mb: 2}}>
                                <Grid item xs={6} lg={4}>
                                    <FormLabel>Email</FormLabel>
                                </Grid>
                                <Grid item xs={6} lg={8}>
                                    <TextField
                                        fullWidth
                                        error={false}
                                        variant="outlined"
                                        value={settings.contact_email}
                                        onChange={(e) => setSettings({...settings, contact_email: e.target.value})}
                                    />
                                </Grid>
                            </Grid>

                            <Grid container spacing={3} alignItems={"baseline"} sx={{mb: 2}}>
                                <Grid item xs={6} lg={4}>
                                    <FormLabel>Teléfono</FormLabel>
                                </Grid>
                                <Grid item xs={6} lg={8}>
                                    <TextField
                                        fullWidth
                                        error={invalidContactPhone}
                                        variant="outlined"
                                        helperText={invalidContactPhone ? "Formato válido: +56912345678 o 56912345678" : ""}
                                        value={settings.contact_phone}
                                        onChange={(e) => setSettings({...settings, contact_phone: normalizePhoneInput(e.target.value)})}
                                    />
                                </Grid>
                            </Grid>

                            <Grid container spacing={3} alignItems={"baseline"} sx={{mb: 2}}>
                                <Grid item xs={6} lg={4}>
                                    <FormLabel>Whatsapp</FormLabel>
                                </Grid>
                                <Grid item xs={6} lg={8}>
                                    <TextField
                                        fullWidth
                                        error={invalidWhatsappPhone}
                                        variant="outlined"
                                        helperText={invalidWhatsappPhone ? "Formato válido: +56912345678 o 56912345678" : ""}
                                        value={settings.whatsapp_number}
                                        onChange={(e) => setSettings({...settings, whatsapp_number: normalizePhoneInput(e.target.value)})}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </React.Fragment>
            )
        }
    }

    const renderNotifSettings = () => {
        if(settings) {
            return (
                <React.Fragment>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Typography variant="h6" sx={{mb: 2}}>Configuración correo SMTP</Typography>

                            <Grid container spacing={3}>
                                <Grid item xs={12}>
                                    <Stack direction={{xs: "column", sm: "row"}} alignItems={{xs: "flex-start", sm: "center"}} spacing={2}>
                                        <FormLabel>Usar mi propio servidor de correo</FormLabel>
                                        <RadioGroup row>
                                            <FormControlLabel value={true} control={<Radio checked={settings.use_custom_mailer === true} onClick={() => setSettings({...settings, use_custom_mailer: true})}/>} label="Si"/>
                                            <FormControlLabel value={false} control={<Radio checked={settings.use_custom_mailer === false} onClick={() => setSettings({...settings, use_custom_mailer: false})}/>} label="No"/>
                                        </RadioGroup>
                                    </Stack>
                                </Grid>

                                {settings.use_custom_mailer && (
                                    <React.Fragment>
                                        <Grid item xs={12} md={6} lg={4}>
                                            <FormLabel sx={{display: "block", mb: 1}}>Servidor de correo saliente</FormLabel>
                                            <TextField
                                                fullWidth
                                                error={false}
                                                variant="outlined"
                                                helperText="Ej: smtp.gmail.com"
                                                value={settings.mailer_smtp_address}
                                                onChange={(e) => setSettings({...settings, mailer_smtp_address: e.target.value})}
                                            />
                                        </Grid>

                                        <Grid item xs={12} md={6} lg={2}>
                                            <FormLabel sx={{display: "block", mb: 1}}>Puerto</FormLabel>
                                            <TextField
                                                fullWidth
                                                error={false}
                                                variant="outlined"
                                                helperText="Ej: 587"
                                                value={settings.mailer_smtp_port}
                                                onChange={(e) => setSettings({...settings, mailer_smtp_port: e.target.value})}
                                            />
                                        </Grid>

                                        <Grid item xs={12} md={6} lg={3}>
                                            <FormLabel sx={{display: "block", mb: 1}}>Dominio</FormLabel>
                                            <TextField
                                                fullWidth
                                                error={false}
                                                variant="outlined"
                                                helperText={`Ej: ${_.kebabCase(settings.name) || 'tudominio' }.com`}
                                                value={settings.mailer_smtp_domain}
                                                onChange={(e) => setSettings({...settings, mailer_smtp_domain: e.target.value})}
                                            />
                                        </Grid>

                                        <Grid item xs={12} md={6} lg={3}>
                                            <FormLabel sx={{display: "block", mb: 1}}>Autenticación</FormLabel>
                                            <Select
                                                value={settings.mailer_smtp_authentication}
                                                fullWidth
                                                variant="outlined"
                                                onChange={(e) => setSettings({...settings, mailer_smtp_authentication: e.target.value})}>
                                                    {renderSelectOpts(['plain','login', 'cram-md5', 'digest-md5', 'ntlm', 'gssapi', 'xoauth', 'xoauth2'])}
                                            </Select>
                                        </Grid>

                                        <Grid item xs={12} md={6} lg={3}>
                                            <Stack direction="row" alignItems="center" spacing={2}>
                                                <FormLabel>StartTLS Auto</FormLabel>
                                                <RadioGroup row>
                                                    <FormControlLabel value={true} control={<Radio checked={settings.mailer_smtp_enable_starttls_auto === true} onClick={() => setSettings({...settings, mailer_smtp_enable_starttls_auto: true})}/>} label="Si"/>
                                                    <FormControlLabel value={false} control={<Radio checked={settings.mailer_smtp_enable_starttls_auto === false} onClick={() => setSettings({...settings, mailer_smtp_enable_starttls_auto: false})}/>} label="No"/>
                                                </RadioGroup>
                                            </Stack>
                                        </Grid>

                                        <Grid item xs={12} md={6} lg={4}>
                                            <FormLabel sx={{display: "block", mb: 1}}>Usuario</FormLabel>
                                            <TextField
                                                fullWidth
                                                error={false}
                                                variant="outlined"
                                                helperText={`Ej: contacto@${_.kebabCase(settings.name) || 'tudominio' }.com`}
                                                value={settings.mailer_smtp_user_name}
                                                onChange={(e) => setSettings({...settings, mailer_smtp_user_name: e.target.value})}
                                            />
                                        </Grid>

                                        <Grid item xs={12} md={6} lg={5}>
                                            <FormLabel sx={{display: "block", mb: 1}}>Contraseña</FormLabel>
                                            <TextField
                                                fullWidth
                                                error={false}
                                                type="password"
                                                variant="outlined"
                                                helperText="Tu contraseña será almacenada encriptada en nuestra base de datos"
                                                value={settings.mailer_smtp_password}
                                                onChange={(e) => setSettings({...settings, mailer_smtp_password: e.target.value})}
                                            />
                                        </Grid>
                                    </React.Fragment>
                                )}
                            </Grid>
                        </Grid>
                    </Grid>
                </React.Fragment>
            )
        }
    }
    
    return (
        <React.Fragment>
            <SettingsCard title="Empresa" isLoading={isLoading}>
                {renderCompanySettings()}
            </SettingsCard>

            <SettingsCard title="Notificaciones y correos electrónicos" isLoading={isLoading} minContentHeight={395}>
                {renderNotifSettings()}
            </SettingsCard>
            
            <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={1}>
                <SaveButton
                    loading={saving}
                    disabled={invalidContactPhone || invalidWhatsappPhone}
                    loadingPosition="end"
                    onClick={() => saveCompanySettings()}>
                        { saving ? 'Guardando' : 'Guardar' }
                </SaveButton>
            </Stack>

        </React.Fragment>
    );
};

export default EditCompany;
